import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store, Select } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    ButtonColors,
    ButtonModule,
    LayerService,
    LayerSize,
    NumberfieldModule,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { ProjectsState } from '../../../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { HeaderService } from 'src/app/shared/services/header.service';
import { Observable } from 'rxjs';
import { PhaseModel, ProjectModel } from '../../../../project.models';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { PhaseUpdateComponent } from './phase-update/phase-update.component';

@UntilDestroy()
@Component({
    selector: 'phase-read',
    templateUrl: './phase-read.component.html',
    styleUrls: ['./phase-read.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
    ],
    providers: [],
})
export class PhaseReadComponent {
    public buttonColors = ButtonColors;

    @Select(ProjectsState.projectWithDetail)
    public project$!: Observable<ProjectModel>;
    public project: ProjectModel | undefined;
    public phase: PhaseModel | undefined;

    @Select(ProjectsState.isLoading)
    public isLoading$!: Observable<boolean>;
    public isLoading: boolean = true;

    public monthlyTimeData = new MatTableDataSource<{}>([]);
    public monthlyTimeColumns: any[] = [];

    public usersData = new MatTableDataSource<{}>([]);
    public usersColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Prénom',
            objectKey: 'firstname',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'lastname',
            bold: true,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Jours passés',
            objectKey: 'daysDeclared',
            bold: false,
        },
    ];
    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly store: Store,
        private readonly layerService: LayerService,
        private readonly sidenavService: SidenavService,
        private readonly headerService: HeaderService,
    ) {
        this.isLoading$
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => (this.isLoading = isLoading));
        this.project$.pipe(untilDestroyed(this)).subscribe((project: ProjectModel) => {
            if (!project) return;
            const code = this.route.snapshot.params['code'];
            const phasecode = this.route.snapshot.params['phasecode'];
            this.project = project;
            if (!this.project) return;
            this.phase = this.project.phases?.find((ph) => ph.code === phasecode);
            if (!this.phase) return;

            this.headerService.setBreadcrumbs([
                { link: `/main/projects/`, label: 'Projets' },
                {
                    link: `/main/projects/${code}/`,
                    label: `${this.project.code} ${this.project.label}`,
                },
                { label: `Phases` },
                {
                    link: `/main/projects/${code}/phase/${phasecode}/`,
                    label: `${this.phase.code} - ${this.phase.label}`,
                },
            ]);

            this.sidenavService.resetSubMenuItem('Projets', [
                {
                    label: 'Tous les projets',
                    icon: 'bi bi-folder2-open',
                    routerLink: ['/main/projects'],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
                {
                    label: `${this.project.code} ${this.project.label}`,
                    icon: 'bi bi-file-earmark-text',
                    routerLink: [`/main/projects/${code}/phase/${phasecode}`],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
            ]);

            const usersData: any[] = [];
            for (const entry of this.phase.dailyTimeEntries) {
                const user = entry.user;
                if (!user) continue;
                const exist = usersData.find((u) => u.id === user.id);
                if (exist) {
                    exist.daysDeclared += entry.percentOfDay;
                } else {
                    usersData.push({
                        id: user.id,
                        firstname: user.firstname,
                        lastname: user.lastname,
                        daysDeclared: entry.percentOfDay || 0,
                    });
                }
            }
            if (this.phase.legacyReportedDays && this.phase.legacyReportedDays > 0) {
                usersData.push({
                    id: null,
                    firstname: 'Import',
                    lastname: 'ASA',
                    daysDeclared: this.phase.legacyReportedDays,
                });
            }
            this.usersData = new MatTableDataSource<{}>(usersData);

            this.generateMonthlyTimeData();
        });
    }

    goToEdition() {
        if (!this.project || !this.phase) return;
        const header = 'Modifier la phase';
        this.layerService.show(
            PhaseUpdateComponent,
            { header, phase: this.phase },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Creating },
        );
    }

    public generateMonthlyTimeData() {
        if (!this.phase) return;
        let monthlyTimeColumns: any[] = [];
        const monthlyTimeData: any[] = [];
        for (const entry of this.phase.dailyTimeEntries) {
            const dateKey: string = `${new Date(entry.date).getFullYear()}-${String(new Date(entry.date).getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyTimeColumns.find((m) => m.objectKey === dateKey)) {
                monthlyTimeColumns.push({
                    displayType: DisplayType.NUMBER,
                    objectDisplayName: dateKey,
                    objectKey: dateKey,
                    bold: false,
                });
            }

            const user = entry.user;
            if (!user) continue;
            const exist = monthlyTimeData.find((u) => u.id === user.id);
            if (exist) {
                exist[dateKey] = (exist[dateKey] ?? 0) + entry.percentOfDay;
                exist.total += entry.percentOfDay;
            } else {
                monthlyTimeData.push({
                    id: user.id,
                    contributorName: `${user.firstname} ${user.lastname} `,
                    [dateKey]: entry.percentOfDay || 0,
                    total: entry.percentOfDay || 0,
                });
            }
        }

        monthlyTimeColumns = monthlyTimeColumns.sort((a, b) =>
            a.objectKey < b.objectKey ? -1 : 1,
        );
        this.monthlyTimeColumns = [
            {
                displayType: DisplayType.TEXT,
                objectDisplayName: 'Contributeur',
                objectKey: 'contributorName',
                bold: false,
            },
            {
                displayType: DisplayType.NUMBER,
                objectDisplayName: 'Total',
                objectKey: 'total',
                bold: false,
            },
            ...monthlyTimeColumns,
        ];

        if (this.phase.legacyReportedDays && this.phase.legacyReportedDays > 0) {
            monthlyTimeData.push({
                id: null,
                contributorName: 'Import ASA',
                total: this.phase.legacyReportedDays,
            });
        }
        this.monthlyTimeData = new MatTableDataSource<{}>(monthlyTimeData);
    }

    public goToContributor(userId: any) {
        if (!userId) return;
        this.router.navigate([`/main/timesheet/contributor/${userId}`]);
    }
}
